// feature/role-views — interactive Insumos tab. Used inside CaseDetail.
// Branches by user role + case state:
//   - insumos/admin + estado in {pendiente_insumos, insumos_solicitados, expediente_solicitado}:
//       request form (Punto 1–4) → on submit, status panel + status dropdown.
//   - everyone else: read-only view of insumo_request.

import React, { useEffect, useState } from "react";
import Spinner from "../../components/Spinner";
import Confirm from "../../components/Confirm";
import Modal from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { insumos, cases as casesApi } from "../../api/endpoints";
import { apiBase } from "../../api/client";
import { rolesOf } from "../../utils/permissions";

const fmt = (d) => d ? new Date(d).toLocaleString("es-CO") : "—";
const fmtDay = (d) => d ? new Date(d).toLocaleDateString("es-CO") : "—";

const SOLICITUD_STATUSES = [
  "SOLICITUD_ENVIADA","SOLICITUD_DE_EXPEDIENTE","SOLICITUD_Y_EXPEDIENTE","SOLICITADO_CON_EXPEDIENTE",
  "N_A_TRATAMIENTO_MEDICO","N_A_EPS","N_A_INPEC","N_A_TRIBUNAL","N_A_FIDUPREVISORA","N_A_CARCEL",
  "N_A_POLICIA","N_A_MAGISTERIO_FOMAG","N_A_FIDUPREVISORA_SA","N_A_FIDUCIARIA_LA_PREVISORA",
  "N_A_JUNTA_REGIONAL_DE_INVA",
  // feature/insumos-solicitud-na-general — catch-all for N A entries
  // when the entity isn't in the predefined list. Key keeps the N_A_
  // prefix so backend cases.js:2540 routes it through the non-state-
  // changing branch alongside the entity-specific siblings; display
  // label is just "N A" per spec.
  "N_A_OTROS",
  "DEVOLVER","NO_SE_ENCUENTRA_CORREO",
];

// Per-key display overrides for the dropdown. Falls back to the
// default underscore-to-space transformation when no override is set.
const SOLICITUD_LABELS = {
  N_A_OTROS: "N A",
};
function solicitudStatusLabel(key) {
  return SOLICITUD_LABELS[key] || key.replaceAll("_", " ");
}

const PROCESSING_STATES = ["pendiente_insumos","insumos_solicitados","expediente_solicitado","insumos_recibidos"];

// feature/response-docs-retire — workflow ownership shifts to Camilo (reparto_insumos)
// once the solicitud is sent. Admin + directora retain it for oversight.
// Insumos team (Natali, César) sees the green handoff banner instead of empty space.
const HANDLE_RESPONSE_ROLES = ["reparto_insumos", "admin", "directora"];
function canHandleResponse(user) {
  const roles = user?.roles?.length ? user.roles : (user?.role ? [user.role] : []);
  return HANDLE_RESPONSE_ROLES.some((r) => roles.includes(r));
}
const HANDOFF_BANNER_ESTADOS = new Set([
  "insumos_solicitados", "expediente_solicitado",
  "insumos_recibidos", "pendiente_reparto_lawyer",
  "asignado_abogado", "cerrado",
]);

export default function InsumosTab({ c, user, onChanged }) {
  // feature/multi-role-arch hotfix.
  // canEdit gates the workflow-control affordances inside RequestStatus
  // (solicitud-status dropdown, "Solicitar expediente al juzgado", and the
  // top-level action cards). Multi-role aware; reparto_insumos and directora
  // need to drive the same controls insumos team does.
  const userRoleList = (user.roles?.length ? user.roles : (user.role ? [user.role] : []));
  const canEdit = userRoleList.some((r) => ["insumos", "admin", "directora", "reparto_insumos"].includes(r));
  const handleResponse = canHandleResponse(user);
  const isInsumosOnly = userRoleList.includes("insumos") && !handleResponse;
  const isProcessing = PROCESSING_STATES.includes(c.estado);
  const noRequestYet = !c.insumo;

  // feature/insumos-validity — validity gate appears for the assigned abogado (or admin)
  // when insumos arrived but haven't been validated yet.
  // feature/single-role-audit — multi-role aware. The abogado-must-own
  // gate is preserved: an abogado-roled user only sees the gate when
  // they're the assigned one. Admins always see it.
  const _itRoles = rolesOf(user);
  const _itIsAdmin = _itRoles.includes("admin");
  const _itIsAbogado = _itRoles.includes("abogado");
  const showValidityGate =
    c.estado === "insumos_recibidos" && c.insumos_validos == null
    && (_itIsAbogado || _itIsAdmin)
    && (!_itIsAbogado || c.abogado_asignado_id === user.id || _itIsAdmin);

  return (
    <div className="col" style={{ gap: 20 }}>
      {showValidityGate && <ValidityGate c={c} onChanged={onChanged} />}

      {/* Request section: form when no request yet (and editable user); status panel after */}
      {canEdit && isProcessing && noRequestYet
        ? <RequestForm c={c} onChanged={onChanged} />
        : <RequestStatus c={c} user={user} canEdit={canEdit && isProcessing} onChanged={onChanged} />}

      {/* feature/fallos-workflow-inversion — handoff to revisor (Molly) once insumos prep is done. */}
      {canEdit && c.tipo === "fallo" && isProcessing && !noRequestYet && (
        <HandoffToRevisor c={c} onChanged={onChanged} />
      )}

      {/* feature/reparto-insumos-dispatcher.
          feature/response-upload-handoff. Workflow controls
          (mark recibido, route to lawyer, response upload) are now scoped
          to reparto_insumos / admin / directora. Insumos team gets the
          handoff banner instead. */}
      {handleResponse
        && ["pendiente_insumos","insumos_solicitados","expediente_solicitado"].includes(c.estado)
        && !noRequestYet
        && c.insumo?.solicitud_status === "SOLICITUD_ENVIADA"
        && c.tipo !== "fallo" && (
        <MarkInsumosReceived c={c} onChanged={onChanged} />
      )}

      {handleResponse
        && ["pendiente_reparto_lawyer","insumos_solicitados","expediente_solicitado",
            "pendiente_insumos","en_elaboracion"].includes(c.estado) && (
        <RouteToLawyer c={c} user={user} onChanged={onChanged} />
      )}

      {/* feature/response-docs-retire — handoff banner for insumos-only users
          once the case has left their step. The team handles court responses
          via external email outside Athena. */}
      {isInsumosOnly && HANDOFF_BANNER_ESTADOS.has(c.estado) && (
        <InsumosHandoffBanner />
      )}
    </div>
  );
}

// feature/response-docs-retire — green "tu trabajo terminó" banner shown to
// insumos-only users when the case has left their step.
function InsumosHandoffBanner() {
  return (
    <div style={{
      display: "flex", gap: 12, alignItems: "flex-start",
      padding: 14,
      background: "var(--accent-mint-bg)",
      borderLeft: "3px solid var(--accent-mint)",
      borderRadius: 4,
      marginTop: 4,
    }}>
      <span style={{ fontSize: 20, color: "var(--accent-mint)", flexShrink: 0, marginTop: 2 }}>✓</span>
      <div>
        <div style={{ fontWeight: 600, fontSize: 14, color: "var(--text-primary)" }}>
          Su trabajo en este caso terminó.
        </div>
        <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>
          El caso está ahora con reparto de insumos (Camilo). El equipo maneja
          las respuestas del juzgado por correo externo.
        </div>
      </div>
    </div>
  );
}

// feature/fallos-workflow-inversion.
// Insumos team marks a fallo "ready for Molly's impugnación review" once the
// solicitud + anexos are prepared. Visible only on tipo='fallo' cases in
// insumos workflow estados.
function HandoffToRevisor({ c, onChanged }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  async function handoff() {
    setBusy(true);
    try {
      await insumos.handoffToRevisor(c.id);
      toast.push("Caso enviado a revisión de Molly");
      setConfirmOpen(false);
      onChanged?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al enviar a revisión", "error");
    } finally { setBusy(false); }
  }

  return (
    <div className="card tight" style={{
      borderLeft: "3px solid var(--accent-mint)",
      paddingLeft: 16,
    }}>
      <h4 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 600 }}>
        Listo para revisión de impugnación
      </h4>
      <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
        Cuando termine de preparar este fallo, marque como listo para que Molly
        revise si se debe impugnar.
      </div>
      <button onClick={() => setConfirmOpen(true)} disabled={busy}>
        {busy && <Spinner color="currentColor" />} Listo para revisión de Molly
      </button>
      {confirmOpen && (
        <Confirm
          open={confirmOpen}
          title="Enviar a revisión de impugnación"
          message="El caso pasará a la cola de Molly y ya no podrá editarse desde insumos. ¿Continuar?"
          onClose={() => setConfirmOpen(false)}
          onConfirm={handoff}
          confirmText={busy ? "Enviando…" : "Enviar a revisión"}
        />
      )}
    </div>
  );
}

// feature/reparto-insumos-dispatcher.
// Insumos team marks Previsora's response received. Transitions case to
// pendiente_reparto_lawyer so it surfaces in Camilo's "Listos para reparto".
function MarkInsumosReceived({ c, onChanged }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  async function markReceived() {
    setBusy(true);
    try {
      await insumos.markReceived(c.id);
      toast.push("Insumos marcados como recibidos. El caso pasó a reparto.");
      setConfirmOpen(false);
      onChanged?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al marcar como recibidos", "error");
    } finally { setBusy(false); }
  }

  return (
    <div className="card tight" style={{
      borderLeft: "3px solid var(--accent-mint)",
      paddingLeft: 16,
    }}>
      <h4 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 600 }}>
        ¿Recibió respuesta de Previsora?
      </h4>
      <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
        Cuando llegue la respuesta con los insumos solicitados, marque aquí. El caso
        pasa al equipo de reparto para enviarlo al abogado correspondiente.
      </div>
      <button onClick={() => setConfirmOpen(true)} disabled={busy}>
        {busy && <Spinner color="currentColor" />} Marcar insumos recibidos
      </button>
      {confirmOpen && (
        <Confirm
          open={confirmOpen}
          title="Marcar insumos recibidos"
          message="¿Confirmar que llegó la respuesta de Previsora? El caso saldrá de su cola y entrará a reparto."
          onClose={() => setConfirmOpen(false)}
          onConfirm={markReceived}
          confirmText={busy ? "Marcando…" : "Confirmar"}
        />
      )}
    </div>
  );
}

// feature/reparto-insumos-dispatcher.
// Camilo (or admin/directora) routes the case to the assigned abogado.
// Standard path: from pendiente_reparto_lawyer (no reason needed).
// Override path: from any pre-Camilo state — reason ≥10 chars enforced.
function RouteToLawyer({ c, user, onChanged }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");

  const roles = (user?.roles && user.roles.length) ? user.roles : (user?.role ? [user.role] : []);
  const allowed = ["admin", "directora", "reparto_insumos"].some((r) => roles.includes(r));
  if (!allowed) return null;

  const noLawyer = !c.abogado_asignado_id;
  const isOverride = c.estado !== "pendiente_reparto_lawyer";
  const reasonOk = !isOverride || reason.trim().length >= 10;

  async function route() {
    if (isOverride && !reasonOk) {
      toast.push("La razón debe tener al menos 10 caracteres.", "error");
      return;
    }
    setBusy(true);
    try {
      await insumos.routeToLawyer(c.id, {
        notes:  notes.trim()  || null,
        reason: isOverride ? reason.trim() : null,
      });
      toast.push(isOverride
        ? "Caso enviado al abogado en override (sin respuesta de Previsora)."
        : "Caso enviado al abogado responsable");
      setOpen(false); setNotes(""); setReason("");
      onChanged?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al hacer reparto", "error");
    } finally { setBusy(false); }
  }

  return (
    <div className="card tight" style={{
      borderLeft: "3px solid var(--accent-mint)",
      paddingLeft: 16,
    }}>
      <h4 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 600 }}>
        Reparto a abogado
      </h4>
      <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
        Revise los insumos recibidos. Si están completos, márquelos como listos
        para el abogado responsable: <strong>{c.abogado_nombre || "—"}</strong>
      </div>
      {noLawyer && (
        <div style={{
          fontSize: 12, color: "var(--color-warning-ink)",
          background: "var(--color-warning-bg)",
          borderLeft: "2px solid var(--color-warning)",
          padding: "6px 10px", borderRadius: 3, marginBottom: 8,
        }}>
          Este caso no tiene abogado responsable asignado. Asígnelo desde la pestaña General antes del reparto.
        </div>
      )}
      <button onClick={() => setOpen(true)} disabled={busy || noLawyer}>
        {busy && <Spinner color="currentColor" />} {isOverride ? "Repartir igualmente" : "Listo para abogado"}
      </button>
      {open && (
        <Modal
          open={open}
          title={isOverride ? "⚠️ Repartir sin respuesta de Previsora" : "Reparto a abogado"}
          onClose={busy ? undefined : () => setOpen(false)}
          footer={<>
            <button className="secondary" disabled={busy} onClick={() => setOpen(false)}>Cancelar</button>
            <button disabled={busy || (isOverride && !reasonOk)} onClick={route}>
              {busy && <Spinner color="currentColor" />} {isOverride ? "Confirmar reparto en override" : "Confirmar reparto"}
            </button>
          </>}
        >
          {isOverride ? (
            <>
              <div style={{
                background: "var(--color-warning-bg)", borderLeft: "2px solid var(--color-warning)",
                padding: 10, borderRadius: 3, fontSize: 13, marginBottom: 12,
              }}>
                Este caso aún no tiene la respuesta de Previsora confirmada. El
                abogado va a recibir el caso sin los insumos completos.
              </div>
              <p style={{ marginTop: 0 }}>
                Caso <strong>{c.case_number}</strong> · estado actual <code>{c.estado}</code>.
                Abogado responsable: <strong>{c.abogado_nombre || "—"}</strong>.
              </p>
              <label style={{ display: "block", textTransform: "none", letterSpacing: 0, fontSize: 13 }}>
                Razón obligatoria (mínimo 10 caracteres)
                <textarea value={reason} onChange={(e) => setReason(e.target.value)}
                          maxLength={500} rows={3}
                          placeholder="Explique por qué el caso debe avanzar sin respuesta de Previsora…"
                          style={{ marginTop: 4, width: "100%" }} autoFocus />
                <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                  {reason.trim().length} / 10 caracteres mínimos
                </div>
              </label>
              <label style={{ marginTop: 12, display: "block", textTransform: "none", letterSpacing: 0, fontSize: 13 }}>
                Notas adicionales (opcional)
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                          maxLength={500} rows={2}
                          style={{ marginTop: 4, width: "100%" }} />
              </label>
            </>
          ) : (
            <>
              <p style={{ marginTop: 0 }}>
                Va a marcar los insumos del caso <strong>{c.case_number}</strong> como
                listos para el abogado responsable: <strong>{c.abogado_nombre || "—"}</strong>.
              </p>
              <label style={{ marginTop: 12, display: "block", textTransform: "none", letterSpacing: 0, fontSize: 13 }}>
                Notas (opcional, máx 500 caracteres)
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                          maxLength={500} rows={3}
                          placeholder="Ej: respuesta completa de Previsora, todos los anexos incluidos…"
                          style={{ marginTop: 4, width: "100%" }} />
              </label>
            </>
          )}
        </Modal>
      )}
    </div>
  );
}

// feature/insumos-validity — yellow banner forcing the abogado to confirm insumos validity
// before they can work the case. "No" routes the case to /admin/devueltos and
// pauses the SLA clock; "Sí" just clears the gate.
function ValidityGate({ c, onChanged }) {
  const toast = useToast();
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);

  async function accept() {
    setBusy(true);
    try {
      await casesApi.update(c.id, { insumos_validos: true });
      toast.push("Insumos confirmados");
      onChanged?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error", "error");
    } finally { setBusy(false); }
  }
  async function reject() {
    if (!motivo.trim()) { toast.push("El motivo es obligatorio", "error"); return; }
    setBusy(true);
    try {
      await casesApi.update(c.id, { insumos_validos: false, insumos_invalidos_motivo: motivo.trim() });
      toast.push("Devuelto a Reparto");
      setShowRejectModal(false);
      onChanged?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error", "error");
    } finally { setBusy(false); }
  }

  return (
    <div className="warn-inline" style={{ padding: 16, borderRadius: 8 }}>
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>
        Antes de continuar: ¿Son válidos los insumos recibidos?
      </div>
      <div className="row" style={{ gap: 8 }}>
        <button onClick={accept} disabled={busy}>
          {busy && <Spinner color="currentColor" />} Sí, son válidos
        </button>
        <button className="secondary" style={{ color: "var(--color-danger)", borderColor: "var(--color-danger)" }}
                onClick={() => setShowRejectModal(true)} disabled={busy}>
          No son válidos
        </button>
      </div>

      {showRejectModal && (
        <Confirm
          open={showRejectModal}
          title="Devolver a Reparto"
          message={
            <div>
              <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
                Explique qué falta o por qué no son válidos los insumos:
              </div>
              <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} autoFocus />
            </div>
          }
          onClose={() => setShowRejectModal(false)}
          onConfirm={reject}
          confirmText={busy ? "Enviando…" : "Devolver a Reparto"}
          danger
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1. Request form (the 4-section Word-template equivalent)
// ---------------------------------------------------------------------------
// feature/intake-multifile — multi-file picker constants shared by intake form
// and post-generation Anexos panel.
const ATTACH_ALLOWED_MIMES = new Set([
  "image/jpeg", "image/png", "image/webp", "image/heic", "image/heif", "application/pdf",
]);
const ATTACH_MAX_FILES = 10;
const ATTACH_MAX_SIZE_BYTES = 10 * 1024 * 1024;

function fmtBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function RequestForm({ c, onChanged }) {
  const toast = useToast();
  const [detalles, setDetalles] = useState("");
  const [soat, setSoat]         = useState("");
  const [cedula, setCedula]     = useState(c.cedula || "");
  const [fechaLimiteOpt, setFechaLimiteOpt] = useState("hoy_5pm");
  const [fechaLimiteCustom, setFechaLimiteCustom] = useState("");
  // feature/intake-multifile — stage files locally; uploaded after solicitud creation.
  // feature/requestform-key-fix. Each picked file is wrapped in
  // { id, file } so we get a stable React key for the rendered list.
  // Previous code used `key={idx}` which shifted on removal, triggering a
  // "Failed to execute 'insertBefore' on 'Node'" reconciliation crash on
  // /insumos/mine (3 hits on the latest bundle in the prod CLIENT ERROR
  // log; 17 more on stale bundles). Counter-based id avoids the rare
  // name+size+lastModified collision drag-drop can produce on duplicates.
  const [selectedFiles, setSelectedFiles] = useState([]); // [{id, file}, …]
  const [isDragOver, setIsDragOver] = useState(false);
  const intakeFileInputRef = React.useRef(null);
  const fileIdRef = React.useRef(0);
  const [busy, setBusy]         = useState(false);

  function validateAndAddFiles(newFiles) {
    const errors = [];
    const valid = [];
    for (const f of newFiles) {
      if (!ATTACH_ALLOWED_MIMES.has(f.type)) { errors.push(`${f.name}: tipo no permitido`); continue; }
      if (f.size > ATTACH_MAX_SIZE_BYTES)    { errors.push(`${f.name}: supera 10 MB`);     continue; }
      valid.push(f);
    }
    const totalAfter = selectedFiles.length + valid.length;
    if (totalAfter > ATTACH_MAX_FILES) {
      const allowed = ATTACH_MAX_FILES - selectedFiles.length;
      if (allowed > 0) {
        errors.push(`Solo se aceptaron ${allowed} archivos (máx 10 total)`);
        valid.splice(allowed);
      } else {
        errors.push("Ya tiene 10 archivos. Quite alguno antes de agregar más.");
        valid.length = 0;
      }
    }
    if (errors.length) toast.push(errors.join(" · "), "error");
    if (valid.length) {
      const wrapped = valid.map((f) => ({ id: ++fileIdRef.current, file: f }));
      setSelectedFiles((prev) => [...prev, ...wrapped]);
    }
  }
  function handleIntakeFileSelect(e) {
    const files = Array.from(e.target.files || []);
    validateAndAddFiles(files);
    if (intakeFileInputRef.current) intakeFileInputRef.current.value = "";
  }
  function handleDragOver(e)  { e.preventDefault(); setIsDragOver(true); }
  function handleDragLeave()  { setIsDragOver(false); }
  function handleDrop(e) {
    e.preventDefault();
    setIsDragOver(false);
    if (selectedFiles.length >= ATTACH_MAX_FILES) {
      toast.push("Ya tiene 10 archivos seleccionados", "error");
      return;
    }
    const files = Array.from(e.dataTransfer.files || []);
    validateAndAddFiles(files);
  }
  function removeFile(id) {
    setSelectedFiles((prev) => prev.filter((item) => item.id !== id));
  }

  function computedFechaLimite() {
    if (fechaLimiteOpt === "hoy_5pm") {
      const d = new Date(); d.setHours(17, 0, 0, 0); return d.toISOString();
    }
    if (fechaLimiteOpt === "manana") {
      const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(17, 0, 0, 0); return d.toISOString();
    }
    if (fechaLimiteOpt === "custom") return fechaLimiteCustom ? new Date(fechaLimiteCustom).toISOString() : null;
    return null;
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      // feature/intake-multifile — three-phase submit: create → upload → regen.
      // Step 1: create the solicitud (no screenshot — attachments handle media now).
      const fd = new FormData();
      if (detalles) fd.append("detalles", detalles);
      if (soat)     fd.append("soat", soat);
      if (cedula)   fd.append("cedula", cedula);
      const fl = computedFechaLimite();
      if (fl)       fd.append("fecha_limite", fl);
      const createRes = await insumos.insumoPdf(c.id, fd);
      const requestId = createRes.data?.request_id;

      // Step 2: upload selected files as attachments. Solicitud already exists —
      // do not roll back if this fails; warn the user instead.
      let regenNeeded = false;
      if (selectedFiles.length > 0 && requestId) {
        try {
          const afd = new FormData();
          // Sprint 54: state holds {id, file} wrappers — pull .file for upload.
          selectedFiles.forEach((item) => afd.append("attachments", item.file));
          await insumos.uploadAttachments(c.id, requestId, afd);
          regenNeeded = true;
        } catch (uploadErr) {
          toast.push("Solicitud creada, pero algunos anexos no se cargaron. Súbalos desde la sección Anexos.", "error");
        }
      }

      // Step 3: regenerate the combined PDF so anexos are included.
      if (regenNeeded) {
        try {
          await insumos.regenerateSolicitudPdf(c.id, requestId);
        } catch (regenErr) {
          toast.push("Anexos cargados, pero el PDF requiere regenerarse manualmente.", "error");
        }
      }

      toast.push("Solicitud generada");
      // feature/insumos-auto-advance. Hint at the next pending
      // case so the user knows there's more queue to process. Best-effort —
      // failure is swallowed so it never blocks the success path.
      try {
        const np = await insumos.nextPending(c.id);
        const nextCase = np.data?.case;
        if (nextCase) {
          toast.push(`Siguiente en cola: ${nextCase.case_number}${nextCase.accionante ? " · " + nextCase.accionante : ""}`);
        } else {
          toast.push("No hay más casos pendientes en su cola.");
        }
      } catch (_) { /* ignore */ }
      onChanged?.();
    } catch (err) {
      toast.push(err?.response?.data?.error || "Error al generar la solicitud", "error");
    } finally { setBusy(false); }
  }

  return (
    <form className="card tight" onSubmit={submit}>
      <h3 style={{ marginTop: 0 }}>Generar solicitud de insumos</h3>

      <Section num={1} title="Datos del proceso">
        <dl className="kvs">
          <dt>Juzgado</dt>     <dd>{c.juzgado || "—"}</dd>
          <dt>Radicado</dt>    <dd>{c.radicado}</dd>
          <dt>Fecha</dt>       <dd>{fmtDay(new Date())}</dd>
          <dt>Ciudad</dt>      <dd>Bogotá D.C.</dd>
          <dt>Accionante</dt>  <dd>{c.accionante_nombre || "—"} {c.cedula && <span className="muted">· CC {c.cedula}</span>}</dd>
          <dt>Accionado</dt>   <dd>{c.flag_na ? (c.entidad_na || "Entidad N/A") : (c.accionado || "Previsora S.A. compañía de seguros")}</dd>
        </dl>
      </Section>

      <Section num={2} title="Detalles de la solicitud">
        <textarea
          value={detalles}
          onChange={(e) => setDetalles(e.target.value)}
          placeholder="Resuma la pretensión y el resuelve. Adjunte la captura del expediente abajo."
        />
        {/* feature/intake-multifile — stage up to 10 files (images + PDF) for upload after creation. */}
        <div style={{ marginTop: 12 }}>
          <label>Anexos (opcional)</label>
          <div className="muted" style={{ fontSize: 11, marginTop: 2, marginBottom: 8 }}>
            Hasta 10 archivos · JPG, PNG, WebP, HEIC o PDF · Máx 10 MB cada uno.
          </div>

          {selectedFiles.length > 0 && (
            <div style={{
              border: "1px solid var(--border-hairline)", borderRadius: 6,
              overflow: "hidden", marginBottom: 10,
            }}>
              {selectedFiles.map((item, idx) => {
                const f = item.file;
                return (
                  <div key={item.id} style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "8px 12px", fontSize: 13,
                    borderBottom: idx === selectedFiles.length - 1 ? "none" : "1px solid var(--border-hairline)",
                  }}>
                    <span aria-hidden="true">{f.type === "application/pdf" ? "📄" : "🖼"}</span>
                    <span style={{
                      flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>{f.name}</span>
                    <span className="muted" style={{ fontSize: 11, whiteSpace: "nowrap" }}>{fmtBytes(f.size)}</span>
                    <button type="button" className="link sm" onClick={() => removeFile(item.id)}
                            style={{ padding: "2px 6px", lineHeight: 1 }} title="Quitar archivo">×</button>
                  </div>
                );
              })}
            </div>
          )}

          <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
               style={{
                 display: "flex", alignItems: "center", gap: 12,
                 padding: "12px 14px",
                 border: `2px dashed ${isDragOver ? "var(--accent)" : "var(--border-hairline)"}`,
                 borderRadius: 6,
                 background: isDragOver ? "var(--accent-soft, rgba(0,0,0,0.02))" : "transparent",
                 transition: "border-color 120ms ease, background 120ms ease",
               }}>
            <button type="button" className="secondary sm"
                    disabled={selectedFiles.length >= ATTACH_MAX_FILES}
                    onClick={() => intakeFileInputRef.current?.click()}>
              {selectedFiles.length === 0 ? "Subir archivos" : `Agregar más (${selectedFiles.length}/${ATTACH_MAX_FILES})`}
            </button>
            <span className="muted" style={{ fontSize: 12 }}>o arrastre y suelte aquí</span>
          </div>

          <input ref={intakeFileInputRef} type="file" multiple
                 accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf"
                 style={{ display: "none" }}
                 onChange={handleIntakeFileSelect} />
        </div>
      </Section>

      <Section num={3} title="Identificación">
        <div className="grid-2">
          <div>
            <label>No. SOAT</label>
            <input value={soat} onChange={(e) => setSoat(e.target.value)} />
          </div>
          <div>
            <label>No. Cédula</label>
            <input value={cedula} onChange={(e) => setCedula(e.target.value)} />
          </div>
        </div>
      </Section>

      <Section num={4} title="Firma">
        <dl className="kvs">
          <dt>Abogado responsable</dt> <dd>{c.abogado_nombre || <span className="muted">—</span>}</dd>
          <dt>Solicitante</dt>         <dd>{/* current user is the requester */}<span>{(typeof window !== "undefined" && JSON.parse(localStorage.getItem("eds.user") || "{}").name) || ""}</span></dd>
          <dt>Fecha</dt>               <dd>{fmtDay(new Date())}</dd>
        </dl>
        <div className="row" style={{ marginTop: 8, flexWrap: "wrap", gap: 16 }}>
          <RadioRow label="Hoy 5pm"   value="hoy_5pm" current={fechaLimiteOpt} setter={setFechaLimiteOpt} />
          <RadioRow label="Mañana"    value="manana"  current={fechaLimiteOpt} setter={setFechaLimiteOpt} />
          <RadioRow label="Otra fecha" value="custom" current={fechaLimiteOpt} setter={setFechaLimiteOpt} />
        </div>
        {fechaLimiteOpt === "custom" && (
          <div style={{ marginTop: 8, maxWidth: 240 }}>
            <input type="datetime-local" value={fechaLimiteCustom} onChange={(e) => setFechaLimiteCustom(e.target.value)} />
          </div>
        )}
      </Section>

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
        <button type="submit" disabled={busy}>
          {busy && <Spinner color="currentColor" />} Generar solicitud
        </button>
      </div>
    </form>
  );
}

function Section({ num, title, children }) {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 11, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--accent-mint)", fontWeight: 600 }}>
        Punto {num}
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", margin: "2px 0 8px" }}>{title}</div>
      {children}
    </div>
  );
}
function RadioRow({ label, value, current, setter }) {
  return (
    <label style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 0, textTransform: "none", fontSize: 13, color: "var(--text-body)", letterSpacing: 0 }}>
      <input type="radio" checked={current === value} onChange={() => setter(value)} /> {label}
    </label>
  );
}

// ---------------------------------------------------------------------------
// 2. Request status (after PDF generated)
// ---------------------------------------------------------------------------
function RequestStatus({ c, canEdit, user, onChanged }) {
  const toast = useToast();
  const ir = c.insumo;
  const [confirmExp, setConfirmExp] = useState(false);
  const [statusChoice, setStatusChoice] = useState(ir?.solicitud_status || "");
  const [busy, setBusy] = useState(false);
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [replaceFile, setReplaceFile] = useState(null);
  // feature/insumos-display-and-edit — Punto 1-4 inline edit + regenerate-pdf state.
  const [regenBusy, setRegenBusy] = useState(false);
  const [pendingChanges, setPendingChanges] = useState(false);
  // feature/insumos-mega — multi-image attachments.
  const [attachments, setAttachments] = useState([]);
  const [attachUploading, setAttachUploading] = useState(false);
  const [lightboxAtt, setLightboxAtt] = useState(null);
  const fileInputRef = React.useRef(null);
  React.useEffect(() => {
    if (!ir?.id) return;
    insumos.listAttachments(c.id, ir.id)
      .then(r => setAttachments(r.data?.attachments || []))
      .catch(() => setAttachments([]));
  }, [ir?.id, c.id]);

  async function uploadFiles(files) {
    if (!files || !files.length) return;
    if (attachments.length + files.length > 10) {
      toast.push(`Máximo 10 archivos. Actuales: ${attachments.length}`, "error");
      return;
    }
    setAttachUploading(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach(f => fd.append("attachments", f));
      const r = await insumos.uploadAttachments(c.id, ir.id, fd);
      setAttachments(prev => [...prev, ...(r.data?.attachments || [])]);
      setPendingChanges(true);
      toast.push(`${(r.data?.attachments || []).length} archivo${(r.data?.attachments || []).length === 1 ? "" : "s"} subido${(r.data?.attachments || []).length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al subir", "error");
    } finally { setAttachUploading(false); }
  }
  async function deleteAttachment(attId) {
    if (!window.confirm("¿Eliminar este archivo del anexo?")) return;
    try {
      await insumos.deleteAttachment(c.id, ir.id, attId);
      setAttachments(prev => prev.filter(a => a.id !== attId));
      setPendingChanges(true);
      toast.push("Archivo eliminado");
    } catch (e) { toast.push(e?.response?.data?.error || "Error", "error"); }
  }

  // feature/permission-gate-widening — gate widened from "creator OR admin OR directora" to
  // include any insumos team member. feature/insumos-auto-assignment round-robin makes the
  // team collaborative, so César needs to fix Natali's typo etc.
  const userRoles = user?.roles && user.roles.length
    ? user.roles
    : (user?.role ? [user.role] : []);
  // feature/reparto-insumos-dispatcher grants reparto_insumos
  // edit access too, but only while the case is still in the insumos phase
  // (pendiente_insumos / insumos_solicitados / expediente_solicitado /
  // insumos_recibidos / pendiente_reparto_lawyer). Past the gate the lawyer
  // owns the case and the field is read-only.
  const PRE_LAWYER_ESTADOS = new Set([
    "pendiente_insumos","insumos_solicitados","expediente_solicitado",
    "insumos_recibidos","pendiente_reparto_lawyer",
  ]);
  const stillInInsumosPhase = !c.estado || PRE_LAWYER_ESTADOS.has(c.estado);
  const canEditSolicitud = !!(
    ir && stillInInsumosPhase && (
      userRoles.includes("admin")
      || userRoles.includes("directora")
      || userRoles.includes("insumos")
      || userRoles.includes("reparto_insumos")
      || ir.created_by_id === user?.id
    )
  );
  const isSent = !!(ir && ir.solicitud_status && ir.solicitud_status !== "");

  const pdfHref = ir?.generated_pdf_path
    ? `${apiBase}/uploads/insumo_pdfs/${ir.generated_pdf_path.split(/[\\/]/).pop()}`
    : null;

  // feature/screenshot-replace — screenshot replace allowed only for the original creator,
  // admin, or directora; only on solicitudes created with the new code path
  // (screenshot_path persisted) and still in pre-response states.
  // feature/single-role-audit — multi-role aware admin/directora check.
  const canReplaceScreenshot = !!(
    ir
    && ir.screenshot_path
    && (["admin","directora"].some(r => rolesOf(user).includes(r)) || ir.created_by_id === user?.id)
    && ["pendiente_insumos", "insumos_solicitados", "expediente_solicitado"].includes(c.estado)
  );
  const screenshotLegacy = !!(ir && !ir.screenshot_path);

  async function doReplaceScreenshot() {
    if (!replaceFile) { toast.push("Seleccione una captura", "error"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("screenshot", replaceFile);
      await insumos.replaceScreenshot(c.id, ir.id, fd);
      toast.push("Captura reemplazada y PDF regenerado");
      setReplaceOpen(false); setReplaceFile(null);
      onChanged?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al reemplazar la captura", "error");
    } finally { setBusy(false); }
  }

  async function applyStatus() {
    if (!statusChoice) return;
    setBusy(true);
    try {
      await casesApi.action(c.id, "mark_solicitud_status", { solicitud_status: statusChoice });
      toast.push("Estado de solicitud actualizado");
      onChanged?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error", "error");
    } finally { setBusy(false); }
  }

  // feature/insumos-display-and-edit — save a single Punto 1-4 metadata field. Case-level fields
  // (juzgado, radicado, accionado, persona name/cédula, termino) go through
  // the existing PATCH /api/cases/:id; SOAT + cédula on the solicitud go
  // through PATCH /api/cases/:id/insumos/:reqId/fields. Either way, mark
  // pendingChanges=true so the user sees the "Regenerar PDF" affordance.
  async function saveCaseField(field, value) {
    try {
      await casesApi.update(c.id, { [field]: value === "" ? null : value });
      toast.push("Guardado");
      setPendingChanges(true);
      onChanged?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al guardar", "error");
      throw e;
    }
  }
  async function saveSolicitudField(field, value) {
    try {
      await insumos.patchSolicitud(c.id, ir.id, { [field]: value === "" ? null : value });
      toast.push("Guardado");
      setPendingChanges(true);
      onChanged?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al guardar", "error");
      throw e;
    }
  }
  async function regeneratePdf() {
    setRegenBusy(true);
    try {
      await insumos.regenerateSolicitudPdf(c.id, ir.id);
      toast.push("PDF regenerado con los cambios");
      setPendingChanges(false);
      onChanged?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al regenerar el PDF", "error");
    } finally { setRegenBusy(false); }
  }

  async function generateExpediente() {
    setBusy(true);
    try {
      await insumos.expedienteLetter(c.id);
      toast.push("Carta al juzgado generada");
      setConfirmExp(false);
      onChanged?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al generar la carta", "error");
    } finally { setBusy(false); }
  }

  if (!ir) {
    return (
      <div className="card tight">
        <h3 style={{ marginTop: 0 }}>Solicitud de insumos</h3>
        <p className="muted">Aún no se ha generado solicitud para este caso.</p>
      </div>
    );
  }

  return (
    <div className="card tight">
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <h3 style={{ margin: 0 }}>Solicitud de insumos</h3>
        <span className="badge mint">{ir.solicitud_status || "—"}</span>
        <span className="muted" style={{ marginLeft: "auto", fontSize: 12 }}>Generada: {fmt(ir.fecha_solicitud)}</span>
      </div>

      {isSent && canEditSolicitud && (
        <div className="warn-inline" style={{ marginTop: 12 }}>
          ⚠️ Esta solicitud ya fue enviada. Si la modifica, recuerde informar al destinatario sobre los cambios.
        </div>
      )}

      <dl className="kvs" style={{ marginTop: 12 }}>
        <dt>Juzgado</dt>
        <dd><SolicitudInline value={c.juzgado} canEdit={canEditSolicitud}
                             onSave={(v) => saveCaseField("juzgado", v)} placeholder="—" /></dd>
        <dt>Radicado</dt>
        <dd><SolicitudInline value={c.radicado} canEdit={canEditSolicitud}
                             onSave={(v) => saveCaseField("radicado", v)} placeholder="—" /></dd>
        {/* feature/accionado-locked — accionado is locked to Previsora; no inline edit. */}
        <dt>Accionado</dt>
        <dd>{c.flag_na ? (c.entidad_na || "Entidad N/A") : (c.accionado || "Previsora S.A. compañía de seguros")}</dd>
        <dt>Fecha límite</dt> <dd>{fmt(ir.fecha_limite)}</dd>
        <dt>SOAT</dt>
        <dd><SolicitudInline value={ir.soat_numero} canEdit={canEditSolicitud}
                             onSave={(v) => saveSolicitudField("soat_numero", v)} placeholder="—" /></dd>
        <dt>Cédula</dt>
        <dd><SolicitudInline value={ir.cedula_numero} canEdit={canEditSolicitud}
                             onSave={(v) => saveSolicitudField("cedula_numero", v)} placeholder="—" /></dd>
      </dl>

      {/* feature/accionado-override. Per-solicitud override at the
          document level. Sprint 31's lock on cases.accionado is unchanged. */}
      {canEditSolicitud && (
        <details style={{
          marginTop: 12, padding: "8px 12px",
          border: "1px solid var(--border-hairline)", borderRadius: 6,
        }}>
          <summary style={{ cursor: "pointer", fontSize: 12, color: "var(--text-muted)" }}>
            ¿Necesita un accionado distinto a Previsora?
            <span className="muted" style={{ marginLeft: 6, fontSize: 11 }}>(opcional)</span>
          </summary>
          <div style={{ paddingTop: 8 }}>
            <div className="muted" style={{ fontSize: 11, marginBottom: 8 }}>
              Por defecto el accionado en el documento es "Previsora S.A. compañía
              de seguros". Si este caso requiere uno distinto (Fiduprevisora, Fomag, etc.),
              escríbalo aquí. Solo afecta este documento — no cambia los datos del caso.
            </div>
            <SolicitudInline
              value={ir.accionado_override}
              canEdit={canEditSolicitud}
              onSave={(v) => saveSolicitudField("accionado_override", v)}
              placeholder="Dejar vacío para usar Previsora S.A."
              maxLength={200}
            />
            {ir.accionado_override && (
              <div style={{
                marginTop: 8, padding: "6px 10px",
                background: "var(--color-warning-bg)",
                borderLeft: "2px solid var(--color-warning)", borderRadius: 3,
                fontSize: 12, display: "flex", alignItems: "center", gap: 8,
              }}>
                <span>El PDF mostrará: <strong>{ir.accionado_override}</strong></span>
                <button className="link sm"
                        style={{ marginLeft: "auto", padding: "2px 6px" }}
                        onClick={() => saveSolicitudField("accionado_override", "")}>
                  Restaurar Previsora
                </button>
              </div>
            )}
          </div>
        </details>
      )}

      {pendingChanges && canEditSolicitud && (
        <div style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "center" }}>
          <button className="secondary sm" disabled={regenBusy} onClick={regeneratePdf}>
            {regenBusy && <Spinner />} Regenerar PDF con los cambios
          </button>
          <span className="muted" style={{ fontSize: 11 }}>
            Hizo cambios. Regenere el PDF para que se reflejen.
          </span>
        </div>
      )}

      {/* feature/insumos-mega — Anexos (multi-image). */}
      <div style={{ marginTop: 24, paddingTop: 16, borderTop: "1px solid var(--border-hairline)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h3 style={{ margin: 0, fontSize: 14 }}>Anexos ({attachments.length}/10)</h3>
          {canEditSolicitud && attachments.length < 10 && (
            <>
              <input ref={fileInputRef} type="file" multiple
                     accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf"
                     style={{ display: "none" }}
                     onChange={(e) => { uploadFiles(e.target.files); if (fileInputRef.current) fileInputRef.current.value = ""; }} />
              <button className="secondary sm" disabled={attachUploading}
                      onClick={() => fileInputRef.current?.click()}>
                {attachUploading && <Spinner />} Subir archivos
              </button>
            </>
          )}
        </div>

        {attachments.length === 0 ? (
          <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
            Sin anexos. Hasta 10 archivos · JPG, PNG, WebP, HEIC o PDF · Máx 10 MB cada uno.
          </div>
        ) : (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
            gap: 10, marginTop: 12,
          }}>
            {attachments.map((a, idx) => (
              <div key={a.id} style={{
                border: "1px solid var(--border-hairline)", borderRadius: 8, overflow: "hidden",
                background: "var(--bg-card)", position: "relative",
              }}>
                <div style={{
                  position: "absolute", top: 6, left: 6, zIndex: 1,
                  background: "rgba(14,42,35,0.85)", color: "#fff",
                  fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 3,
                }}>{idx + 1}</div>
                {a.mime_type === "application/pdf" ? (
                  <div
                    onClick={() => window.open(apiBase + insumos.attachmentFileUrl(c.id, ir.id, a.id), "_blank")}
                    style={{
                      width: "100%", aspectRatio: "1", cursor: "pointer",
                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                      background: "linear-gradient(135deg, var(--color-hover-bg) 0%, var(--bg-subtle) 100%)",
                      color: "var(--accent-mint)",
                    }}
                    title="Abrir PDF en nueva pestaña"
                  >
                    <svg width="40" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                    <span style={{ fontSize: 11, fontWeight: 600, marginTop: 6, letterSpacing: 0.5 }}>PDF</span>
                    {a.page_count != null && (
                      <span className="muted" style={{ fontSize: 10, marginTop: 2 }}>{a.page_count} pág.</span>
                    )}
                  </div>
                ) : (
                  <img
                    src={apiBase + insumos.attachmentFileUrl(c.id, ir.id, a.id)}
                    alt={a.original_filename}
                    style={{ width: "100%", aspectRatio: "1", objectFit: "cover", cursor: "zoom-in", display: "block" }}
                    onClick={() => setLightboxAtt(a)}
                  />
                )}
                <div style={{ padding: "6px 8px", display: "flex", alignItems: "center", gap: 4 }}>
                  <span className="muted" style={{ fontSize: 10, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        title={a.original_filename}>{a.original_filename}</span>
                  {canEditSolicitud && (
                    <button className="ghost sm" style={{ padding: "0 4px", height: 22, color: "var(--color-danger)" }}
                            onClick={() => deleteAttachment(a.id)} title="Eliminar">×</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {lightboxAtt && (
          <div onClick={() => setLightboxAtt(null)}
               style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
                        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, cursor: "zoom-out" }}>
            <img src={apiBase + insumos.attachmentFileUrl(c.id, ir.id, lightboxAtt.id)}
                 alt={lightboxAtt.original_filename}
                 style={{ maxWidth: "95vw", maxHeight: "95vh", cursor: "default" }}
                 onClick={(e) => e.stopPropagation()} />
          </div>
        )}
      </div>

      {/* feature/screenshot-replace — replace the original screenshot, regenerates the PDF. */}
      {(canReplaceScreenshot || screenshotLegacy) && (
        <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10 }}>
          {canReplaceScreenshot && (
            <button className="secondary sm" type="button" onClick={() => { setReplaceFile(null); setReplaceOpen(true); }}>
              Reemplazar captura
            </button>
          )}
          {screenshotLegacy && (
            <span className="muted" style={{ fontSize: 11 }}>
              Captura no editable — solicitud creada antes de mayo 2026.
            </span>
          )}
        </div>
      )}

      {pdfHref && (
        <div style={{ marginTop: 12 }}>
          <iframe title="Solicitud PDF" src={pdfHref} style={{ width: "100%", height: 480, border: "1px solid var(--border-hairline)", borderRadius: 8, background: "var(--bg-card)" }} />
          <div style={{ marginTop: 6 }}>
            <a href={pdfHref} target="_blank" rel="noreferrer">Abrir en nueva pestaña</a>
          </div>
        </div>
      )}

      {replaceOpen && (
        <Confirm
          open={replaceOpen}
          title="Reemplazar captura"
          message={
            <div>
              <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
                La nueva captura sustituye la anterior y se regenera el PDF combinado.
              </div>
              <input type="file" accept="image/*,application/pdf"
                     onChange={(e) => setReplaceFile(e.target.files?.[0] || null)} />
            </div>
          }
          onClose={() => { setReplaceOpen(false); setReplaceFile(null); }}
          onConfirm={doReplaceScreenshot}
          confirmText={busy ? "Reemplazando…" : "Reemplazar y regenerar"}
        />
      )}

      {canEdit && (
        <>
          <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid var(--border-hairline)" }}>
            <label>Marcar estado de solicitud</label>
            <div className="row">
              <select value={statusChoice} onChange={(e) => setStatusChoice(e.target.value)}>
                <option value="">— elegir —</option>
                {SOLICITUD_STATUSES.map(s => <option key={s} value={s}>{solicitudStatusLabel(s)}</option>)}
              </select>
              <button className="secondary" disabled={busy || !statusChoice} onClick={applyStatus}>
                {busy && <Spinner />} Actualizar
              </button>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <button className="secondary" onClick={() => setConfirmExp(true)}>Solicitar expediente al juzgado</button>
          </div>

          <Confirm
            open={confirmExp}
            title="Generar carta de expediente"
            message="Se generará una carta solicitando el expediente completo al juzgado. El estado del caso pasará a expediente_solicitado."
            onClose={() => setConfirmExp(false)}
            onConfirm={generateExpediente}
            confirmText="Generar carta"
          />
        </>
      )}
    </div>
  );
}

// ===========================================================================
// feature/insumos-display-and-edit — Punto 1-4 inline editors.
// SolicitudInline:   plain text field with pencil-icon hover-reveal.
// AccionadoInline:   dropdown (Previsora / Fiduprevisora / Fomag / Otro…)
// Saves through a parent-supplied onSave(value). Enter saves, Esc cancels,
// blur saves. Minimal — adopts the same affordances as feature/inline-edit-soft-delete InlineEdit
// without coupling to it.
// ===========================================================================
function SolicitudInline({ value, canEdit, onSave, placeholder }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value || "");
  const [busy, setBusy] = useState(false);
  if (!editing) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span>{value || <span className="muted">{placeholder || "—"}</span>}</span>
        {canEdit && (
          <button className="ghost sm" style={{ padding: "0 6px", height: 22 }}
                  title="Editar" aria-label="Editar"
                  onClick={() => { setV(value || ""); setEditing(true); }}>✎</button>
        )}
      </span>
    );
  }
  async function save() {
    setBusy(true);
    try { await onSave(v); setEditing(false); }
    catch { /* keep edit open on error */ }
    finally { setBusy(false); }
  }
  return (
    <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
      <input
        autoFocus value={v} disabled={busy}
        onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          else if (e.key === "Escape") setEditing(false);
        }}
        onBlur={save}
        style={{ minWidth: 200 }}
      />
      <button className="ghost sm" disabled={busy} onClick={() => setEditing(false)}>Cancelar</button>
    </span>
  );
}

const ACCIONADO_OPTIONS = [
  { value: "Previsora S.A. compañía de seguros", label: "Previsora S.A. compañía de seguros" },
  { value: "Fiduprevisora S.A.",                 label: "Fiduprevisora S.A." },
  { value: "Fomag",                              label: "Fomag" },
  { value: "__custom__",                         label: "Otro…" },
];
function AccionadoInline({ value, rawValue, canEdit, onSave }) {
  const [editing, setEditing] = useState(false);
  const presetMatch = ACCIONADO_OPTIONS.find(o => o.value === rawValue);
  const [choice, setChoice] = useState(presetMatch ? rawValue : "__custom__");
  const [custom, setCustom] = useState(presetMatch ? "" : (rawValue || ""));
  const [busy, setBusy] = useState(false);

  if (!editing) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span>{value}</span>
        {canEdit && (
          <button className="ghost sm" style={{ padding: "0 6px", height: 22 }}
                  title="Editar" aria-label="Editar"
                  onClick={() => {
                    const m = ACCIONADO_OPTIONS.find(o => o.value === rawValue);
                    setChoice(m ? rawValue : "__custom__");
                    setCustom(m ? "" : (rawValue || ""));
                    setEditing(true);
                  }}>✎</button>
        )}
      </span>
    );
  }
  async function save() {
    const finalVal = choice === "__custom__" ? custom.trim() : choice;
    if (!finalVal) return;
    setBusy(true);
    try { await onSave(finalVal); setEditing(false); }
    catch { /* keep open on error */ }
    finally { setBusy(false); }
  }
  return (
    <span style={{ display: "inline-flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
      <select value={choice} onChange={(e) => setChoice(e.target.value)} disabled={busy} autoFocus>
        {ACCIONADO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {choice === "__custom__" && (
        <input value={custom} onChange={(e) => setCustom(e.target.value)}
               placeholder="Otro accionado…" disabled={busy} style={{ minWidth: 220 }} />
      )}
      <button className="sm" disabled={busy} onClick={save}>Guardar</button>
      <button className="ghost sm" disabled={busy} onClick={() => setEditing(false)}>Cancelar</button>
    </span>
  );
}
